import { inject, Injectable } from '@angular/core';

import { map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Observable, of } from 'rxjs';
import { ApplicationsApiActions, VppApplicationsResolverActions } from './vpp-applications.actions';

import { AppleApplicationsService, AppleVppService, VppAsset } from '@rocksoft/mdm-sdk-v2';
import type { VppApplication } from '@rocksoft/shared';
import { safeMap } from '../effects.helpers';
import { VppApplicationsRequestService } from './vpp-applications-request-service';

@Injectable()
export class VppApplicationsEffects {
  private readonly actions = inject(Actions);
  private readonly applicationService = inject(AppleApplicationsService);
  private readonly vppService = inject(AppleVppService);
  private readonly vppApplicationsRequestService = inject(VppApplicationsRequestService);

  /**
   * The (re)load of the vpp applications has been requested.
   * Effect: we load the state from the api.
   */
  vppApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(VppApplicationsResolverActions.vppApplicationsRequested),
      switchMap(() =>
        safeMap(
          this.applicationService.getVppAssets({ apiVersion: '2.0' }).pipe(
            switchMap((apiVppAssets) => this.getVppAssetsMetadata(apiVppAssets)),
            map((assetsWithMetadata) => assetsWithMetadata.filter((assetWithMetadata) => !!assetWithMetadata.bundleId))
          ),
          (vppApplications) =>
            ApplicationsApiActions.vppApplicationsLoaded({
              applications: vppApplications,
            }),
          (error) => ApplicationsApiActions.vppApplicationsLoadedError({ error })
        )
      )
    );
  });

  /**
   * The CSV file download of the VPP applications has been requested.
   * Effect: we load the state from the api.
   */
  vppApplicationsCsvRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(VppApplicationsResolverActions.vppApplicationsCSVDownloadRequested),
      mergeMap(() =>
        safeMap(
          this.vppApplicationsRequestService.getAllScheduledVppApplicationsCsv().pipe(
            tap((res: Blob) => {
              const today = new Date();
              const month: string = (today.getMonth() + 1).toString();
              const day: string = today.getDate().toString();
              const year: string = today.getFullYear().toString();
              const formattedDate = `${month}-${day}-${year}`;
              this.createDownloadFile(res, 'text/csv', `scheduled-vpp-applications-overview-${formattedDate}`);
            })
          ),
          () => ApplicationsApiActions.vppApplicationsCSVDownloaded(),
          (error) => ApplicationsApiActions.vppApplicationsCSVDownloadedError({ error })
        )
      )
    );
  });

  private createDownloadFile(res: Blob, type: string, name: string) {
    // Create a new blob to add the file extension
    const blob = new Blob([res], { type: type });
    // Trigger the download
    const uri = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.download = name;
    anchor.href = uri;
    anchor.click();
  }

  private getVppAssetsMetadata = (vppAssets: VppAsset[]): Observable<VppApplication[]> => {
    const adamIds = vppAssets
      .map((vppAsset) => parseInt(vppAsset.adamIdStr))
      .filter((adamId) => Number.isFinite(adamId));

    if (!adamIds.length) {
      return of([]);
    }

    return this.vppService.getVppApplicationsMetadata({ apiVersion: '2.0', adamIds }).pipe(
      map((res: VppApplication[]) => {
        return res.map((metadata) => this.mapVppApplicationMetadata(metadata));
      })
    );
  };

  private mapVppApplicationMetadata = (metadata: VppApplication) => {
    return {
      bundleId: metadata.bundleId,
      name: metadata.name,
      version: metadata.version,
      iconUrl: metadata.iconUrl,
      vppAsset: metadata.vppAsset,
      adamIdStr: metadata.vppAsset?.adamIdStr ?? '',
    };
  };
}
